
  import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
  import { IModalComponent, DropdownField, Btn, Field, TextareaField, FormButtonDisabler } from '@samc/centricity-core';
  import { IBuyer, IBuyPackageList, IDealBuyerInfo, IMovingLoansRequest, IPackage, ISeller } from '@/Interfaces';
  import { EligibleTargetDealBuyerSource, EligibleTargetDealsSource, PackageSource, BuyerSource } from '@/Sources';

  @Component({
    name: 'move-loans-modal',
    components: { DropdownField, TextareaField, Btn, Field, FormButtonDisabler }
  })
  export default class MoveLoansModal extends Vue implements IModalComponent {
    @Prop({ required: true }) public sourcePackageId!: string;
    @Prop({ required: false }) public sourcePackageStatus!: string;
    @Prop({ required: true }) public buyer!: IBuyer;
    @Prop({ required: false }) public seller: ISeller | undefined;
    @Prop({ required: true }) public packageType!: 'buy' | 'sell';
    @Prop({ required: true }) public loanIds!: number[];
    @Prop({ required: false, default: false }) public sourceLoanLevelPricing!: boolean;
    @Prop({ required: false, default: false }) public preservePricing!: boolean;

    @Prop() public addCloseProp!: (callback: () => Promise<any>) => void;
    @Prop() public closeProp!: () => void;

    public get packageTypeName(): 'Package' | 'Deal' {  return this.packageType === 'buy' ? 'Package' : 'Deal';}

    public buyerSource: EligibleTargetDealBuyerSource | BuyerSource | IBuyer[] = [];
    public description: string = '';
    public package: IBuyPackageList | IDealBuyerInfo | null = null;
    public packageSource: PackageSource | EligibleTargetDealsSource | IPackage[] = [];
    public selectedBuyer: IBuyer | null = null;

    public get disableBuyer(): boolean { return (this.buyer.id?.length > 0 && this.sourcePackageStatus !== 'Bid'); }
    public get packageId(): string {
      const packageId = this.packageType === 'buy'
        ? (this.package as IBuyPackageList)?.id
        : (this.package as IDealBuyerInfo)?.dealId;

      return packageId ?? '';
    }

    @Watch('package')
    public updateBuyer(newPackage: IBuyPackageList | IDealBuyerInfo | null) {
      if (this.packageType === 'sell' && !this.disableBuyer || newPackage !== null) {
        const deal = newPackage as IDealBuyerInfo;
        this.selectedBuyer = { id: deal.buyerId as string, name: deal.buyerName };
      }
      if (this.packageType === 'buy' && !this.disableBuyer) {
        const pkg = newPackage as IBuyPackageList;
        this.selectedBuyer = { id: pkg.buyerId as string, name: pkg.buyerName };
      }
    }

    @Watch('selectedBuyer')
    public async updateDealSourceBuyer(newBuyer: IBuyer) {
      if (this.packageType === 'sell' && !this.disableBuyer) {
        const dealSource = (this.packageSource as EligibleTargetDealsSource);
        if (dealSource.buyerId !== newBuyer.id) {
          dealSource.buyerId = newBuyer.id;
          await dealSource.read();
        }
      }
      else if (this.packageType === 'buy' && !this.disableBuyer) {
       this.packageSource = new PackageSource(this.seller?.id, newBuyer.id, null, this.sourcePackageStatus);
      }
    }

    public async mounted() {
      this.selectedBuyer = this.buyer.id?.length > 0 ? this.buyer : null;

      if (this.packageType === 'buy') {
        this.packageSource = new PackageSource(this.seller?.id, this.buyer.id, null, this.sourcePackageStatus);
        if (this.sourcePackageStatus === 'Bid') {
         this.buyerSource = new BuyerSource(true);
        }
        else {
          this.buyerSource = [this.buyer];
        }
      } 
      else if (this.disableBuyer) {
        this.packageSource = new EligibleTargetDealsSource(this.loanIds, this.sourcePackageId);
        this.buyerSource = [this.buyer];
      } 
      else {
        this.packageSource = new EligibleTargetDealsSource(this.loanIds, this.sourcePackageId, this.selectedBuyer?.id);
        this.buyerSource = new EligibleTargetDealBuyerSource(this.loanIds, this.sourcePackageId);
      }

      this.addCloseProp(async () => {
        const messages: string[] = [];
        if (this.packageId?.length === 0) messages.push(this.packageTypeName + ' is required');
        if (this.sourcePackageId === this.packageId) messages.push('Loans cannot be moved to the same package please choose a new ' + this.packageTypeName);

        const targetLoanLevelPricing = this.packageType === 'buy'
          ? (this.package as IBuyPackageList)?.loanLevelPricingIndicator
          : (this.package as IDealBuyerInfo)?.loanLevelPricingIndicator;

        var warningMessage = '';

        if (this.preservePricing)
        {
          if (targetLoanLevelPricing)
          {
            if (this.sourceLoanLevelPricing)
            {
              warningMessage = `Moving loans into this trade while retaining their pricing will cause the destination trade to recalculate weighted average pricing. Proceed?`;
            }
            else
            {
              warningMessage = `Moving loans into this trade while retaining their pricing will cause these loans to adopt a loan level price consistent with their current trade pricing. Proceed?`;
            }
          }
          else
          {
            warningMessage = `Moving loans into this trade while retaining their pricing will cause the destination trade to convert to a loan level pricing trade. Proceed?`;
          }
        }
        else
        {
          if (targetLoanLevelPricing) 
          {
            if (this.sourceLoanLevelPricing)
            {
              warningMessage = `Moving loans without their pricing will cause these loans to lose their existing loan level pricing. Proceed?`;
            }
            else
            {
              warningMessage = `Moving loans without their pricing will cause these loans to lose their existing trade level pricing. Proceed?`;
            }
          }
          else 
          {
            warningMessage = `Moving loans without their pricing into a trade with trade level pricing will cause these loans to adopt the trade price of their new trade. Proceed?`;
          }
        }

        const confirm = await window.showConfirm?.({ 
          title: 'Loans Pricing Confirmation',
          description: warningMessage,
          type: 'warning',
          hasOverlay: true
        })

        if ((confirm ?? false) === false) return;

        if (messages.length === 0) {
          return { targetPackageId: this.packageId, description: this.description, loansToMove: this.loanIds, preservePricing: this.preservePricing } as IMovingLoansRequest;
        }

        this.$alert({
          title: 'Validation Errors Occurred',
          description: 'Please read below for details',
          type: 'error',
          errors: messages.slice(0, 10)
        });
      });
    }
  }

